import type { ChatbotLogger } from '../../types';
import { GIGA_CONFIG_GLOBAL, GIGA_SCRIPT_ID, GIGA_SCRIPT_SRC, GIGA_WIDGET_ROOT_ID } from './config';
import type { GigaWidgetConfig } from './types';

/**
 * Sets `window.CHAT_WIDGET_CONFIG` and appends Giga's self-injecting loader to `<head>`.
 * Per Giga's SPA guide, the config must exist before the script runs, so this does both together.
 */
export function injectGigaWidget(
  config: GigaWidgetConfig,
  logger: ChatbotLogger | Console = console,
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    (window as Window)[GIGA_CONFIG_GLOBAL] = config;

    if (document.getElementById(GIGA_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = GIGA_SCRIPT_ID;
    script.src = GIGA_SCRIPT_SRC;
    script.defer = true;
    script.onerror = () =>
      logger.error({
        type: 'client',
        context: 'Chatbot - giga.injectGigaWidget',
        message: 'Giga loader failed to load (blocked or network error)',
        sessionId: undefined,
      });
    document.head.appendChild(script);

    logger.info({
      type: 'client',
      context: 'Chatbot - giga.injectGigaWidget',
      message: 'Config set and loader injected',
      sessionId: undefined,
    });
  } catch (err) {
    logger.error(
      {
        type: 'client',
        context: 'Chatbot - giga.injectGigaWidget',
        message: 'Failed to inject Giga widget',
        sessionId: undefined,
      },
      { error: err },
    );
  }
}

/**
 * Tears Giga down: removes the loader, every node it injected, and the config. The loader has no
 * destroy API, so removing only the `<script>` would orphan the live widget. Giga namespaces its
 * DOM (the `gigaml-container` root plus any launcher/overlay/style it adds) with the `gigaml`
 * namespace, so sweeping any node whose id or class contains it prevents orphaned nodes
 * accumulating across remounts.
 */
export function removeGigaWidget(logger: ChatbotLogger | Console = console): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    document.getElementById(GIGA_SCRIPT_ID)?.remove();
    document.querySelectorAll('[id*="gigaml"], [class*="gigaml"]').forEach((node) => node.remove());
    delete (window as Window)[GIGA_CONFIG_GLOBAL];

    logger.info({
      type: 'client',
      context: 'Chatbot - giga.removeGigaWidget',
      message: 'Loader removed and config cleared',
      sessionId: undefined,
    });
  } catch (err) {
    logger.error(
      {
        type: 'client',
        context: 'Chatbot - giga.removeGigaWidget',
        message: 'Failed to remove Giga widget',
        sessionId: undefined,
      },
      { error: err },
    );
  }
}

/**
 * Whether Giga is live — the widget root it builds is in the DOM. The loader `<script>` alone isn't
 * enough: it may be blocked (ad blocker / CSP) or fail to load, in which case no widget is ever built.
 */
export function isGigaMounted(): boolean {
  if (typeof document === 'undefined') return false;
  return !!document.getElementById(GIGA_WIDGET_ROOT_ID);
}
